import React, { useId, useLayoutEffect, useRef, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';

import { localMemberLabel } from '~/utils/localFilterOptions';

import ConfidenceBadge, { StalePredictionContext } from './ConfidenceBadge';
import {
  confidenceSelectionText,
  confidenceTierLabels,
  confidenceTiers,
  toggleConfidenceTier,
} from './confidenceSelection';
import type { ConfidenceTier } from './types';

type Props = {
  value: ConfidenceTier[];
  onChange: (value: ConfidenceTier[]) => void;
  showDescriptions?: boolean;
  badge?: boolean;
  available?: ConfidenceTier[];
  complete?: boolean;
};

export default function ConfidenceSelect({
  value,
  onChange,
  showDescriptions = false,
  badge = false,
  available = confidenceTiers,
  complete = false,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  // Drill selections update the router asynchronously. Keep successive ticks cumulative until it renders.
  const selection = useRef(value);
  useLayoutEffect(() => {
    selection.current = value;
  }, [value]);
  const choose = (next: ConfidenceTier[]) => {
    selection.current = next;
    onChange(next);
  };
  const text = value.some((tier) => complete && !available.includes(tier))
    ? value.map((tier) => localMemberLabel(tier, available, complete, confidenceTierLabels[tier])).join(', ')
    : confidenceSelectionText(value);
  const close = () => setAnchor(null);
  const keys = (event: React.KeyboardEvent) => {
    const options = Array.from(list.current?.querySelectorAll<HTMLElement>('[role=option]') ?? []);
    const index = options.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    }
  };
  return (
    <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
      <InputLabel shrink id={`${id}-label`} sx={{ bgcolor: 'background.paper', px: 0.5 }}>
        Confidence
      </InputLabel>
      <ButtonBase
        ref={trigger}
        aria-label={`Confidence: ${text}`}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? id : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          height: 40,
          width: '100%',
          minWidth: 0,
          px: 1.5,
          gap: 0.75,
          border: 1,
          borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'),
          borderRadius: 1,
          justifyContent: 'flex-start',
          '&:hover': { borderColor: 'text.primary' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <InfoOutlinedIcon fontSize="small" />
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {badge && value.length === 1 && !(complete && !available.includes(value[0])) ? (
            <StalePredictionContext.Provider value={null}>
              <ConfidenceBadge tier={value[0]} tabIndex={-1} />
            </StalePredictionContext.Provider>
          ) : (
            text
          )}
        </Box>
        <KeyboardArrowDownIcon fontSize="small" />
      </ButtonBase>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          // Wait for the modal focus trap to unmount before returning keyboard focus.
          transition: { onExited: () => requestAnimationFrame(() => trigger.current?.focus()) },
          paper: {
            sx: { width: showDescriptions ? 360 : 260, maxWidth: 'calc(100vw - 32px)' },
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Tab') {
                event.preventDefault();
                close();
              }
            },
          },
        }}
      >
        <Typography sx={{ px: 1.5, pt: 1 }} variant="subtitle2">
          All confidence tiers
        </Typography>
        <List
          id={id}
          ref={list}
          role="listbox"
          aria-labelledby={`${id}-label`}
          aria-multiselectable
          dense
          onKeyDown={keys}
        >
          {confidenceTiers
            .filter((tier) => available.includes(tier) || value.includes(tier))
            .map((tier, index) => (
              <ListItemButton
                key={tier}
                autoFocus={index === 0}
                role="option"
                aria-selected={value.includes(tier)}
                selected={value.includes(tier)}
                onClick={() => choose(toggleConfidenceTier(selection.current, tier))}
              >
                <Checkbox
                  checked={value.includes(tier)}
                  size="small"
                  tabIndex={-1}
                  inputProps={{ 'aria-hidden': true }}
                />
                <ListItemText
                  primary={localMemberLabel(tier, available, complete, confidenceTierLabels[tier])}
                  secondary={
                    showDescriptions
                      ? tier === 'indicative'
                        ? 'Far-out, empty or stale-data estimate; shown for context, never triggers Needs Action.'
                        : 'How often predictions land within ±2 travellers (or 10% of seats).'
                      : undefined
                  }
                  slotProps={{ primary: { fontSize: 13 }, secondary: { fontSize: 12 } }}
                />
                <Button
                  size="small"
                  aria-label={`Only ${confidenceTierLabels[tier]}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    choose([tier]);
                  }}
                >
                  Only
                </Button>
              </ListItemButton>
            ))}
        </List>
        <Typography
          color="text.secondary"
          sx={{ borderTop: 1, borderColor: 'divider', fontSize: 11.5, px: 1.5, py: 0.5 }}
        >
          Applies as you tick
        </Typography>
        {value.length > 0 && (
          <Stack direction="row" sx={{ borderTop: 1, borderColor: 'divider', px: 1 }}>
            <Button size="small" onClick={() => choose([])}>
              Clear
            </Button>
          </Stack>
        )}
      </Popover>
    </FormControl>
  );
}
